import React from 'react';
import PageWrapper from '../components/PageWrapper';

export const Services = ({ onNavigate }) => (
  <PageWrapper onNavigate={onNavigate} showFullFooter>
    <div className="page-hero">
      <p className="section-label">What We Do</p>
      <h1>Our Services</h1>
      <p>Comprehensive consulting solutions across Africa's key growth sectors.</p>
    </div>
    <section className="section">
      <div className="container">
        <div className="grid-2">
          {[
            { icon: '⛏', title: 'Mining Consultancy', page: 'mining', items: ['Exploration & Geological Studies', 'Environmental Impact Assessment', 'Regulatory Licensing & Compliance', 'Mine Planning & Development', 'Community Relations Management'] },
            { icon: '◆', title: 'Diamond Services', page: 'diamonds', items: ['Ethical Sourcing & Grading', 'Kimberley Process Compliance', 'Market Access & Trade Facilitation', 'Valuation & Certification', 'Investment Advisory'] },
            { icon: '♻', title: 'Renewable Energy', page: 'renewables', items: ['Feasibility & Resource Assessment', 'Project Financing & Investment', 'Government Negotiation & PPA', 'EPC Procurement', 'Operations Support'] },
            { icon: '🌿', title: 'Eco Tourism', page: 'ecotourism', items: ['Safari & Wildlife Lodge Development', 'Conservation Planning', 'Community-Based Tourism', 'Investment Structuring', 'Marketing & Branding'] },
          ].map(s => (
            <div key={s.title} className="card card-gold-top">
              <span className="card-icon">{s.icon}</span>
              <h3>{s.title}</h3>
              <ul className="service-list">
                {s.items.map(i => <li key={i}>{i}</li>)}
              </ul>
              <button className="btn-gold" style={{ marginTop: '1.5rem', padding: '.65rem 1.5rem', fontSize: '.72rem' }} onClick={() => onNavigate(s.page)}>Learn More →</button>
            </div>
          ))}
        </div>
      </div>
    </section>
  </PageWrapper>
);

export const Mining = ({ onNavigate }) => (
  <PageWrapper onNavigate={onNavigate} showFullFooter>
    <div className="page-hero" style={{ background: 'linear-gradient(135deg, #1a1a0d, #2d3d1a)' }}>
      <p className="section-label">Sector</p>
      <h1>Mining Consultancy</h1>
      <p>From earth to export — comprehensive mining solutions across Africa.</p>
    </div>
    <section className="section">
      <div className="container">
        <div className="grid-2" style={{ alignItems: 'start', gap: '3rem' }}>
          <div>
            <p className="section-label">Overview</p>
            <h2 className="section-title">Unearthing Africa's Mineral Wealth</h2>
            <div className="gold-line" />
            <p style={{ color: 'var(--muted)', lineHeight: 1.9, marginBottom: '2rem' }}>
              Africa holds approximately 30% of the world's mineral resources — including gold, platinum, chromite, manganese, and more. Varahi brings the expertise to unlock these deposits responsibly and profitably.
            </p>
            <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--green-dark)', marginBottom: '1rem', fontSize: '1.3rem' }}>What We Offer</h3>
            <ul className="service-list">
              {['Geological mapping and mineral exploration programs', 'Feasibility studies and resource estimation', 'Environmental and social impact assessments', 'Mining license acquisition and regulatory compliance', 'Mine design, planning and project management', 'Community engagement and benefit-sharing structures', 'Exit strategy and divestment advisory'].map(i => <li key={i}>{i}</li>)}
            </ul>
          </div>
          <div>
            <div style={{ background: '#071a12', padding: '2rem', marginBottom: '1.5rem' }}>
              <h3 style={{ color: '#c9a84c', fontFamily: "'Playfair Display', serif", marginBottom: '1.25rem' }}>Key Minerals We Cover</h3>
              <div className="grid-2" style={{ gap: '1rem' }}>
                {[['🥇', 'Gold'], ['⬛', 'Coal'], ['🔩', 'Iron Ore'], ['🪨', 'Platinum'], ['💎', 'Chromite'], ['🟤', 'Bauxite']].map(([ico, name]) => (
                  <div key={name} style={{ background: 'rgba(201,168,76,.1)', border: '1px solid rgba(201,168,76,.2)', padding: '1rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.5rem', marginBottom: '.4rem' }}>{ico}</div>
                    <p style={{ color: '#fff', fontSize: '.85rem' }}>{name}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="card" style={{ borderLeft: '4px solid #c9a84c' }}>
              <h4 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--green-dark)', marginBottom: '.5rem' }}>Our Track Record</h4>
              <p>Over 60 mining projects successfully facilitated across 12 African countries, representing over $1.2 billion in investment value.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </PageWrapper>
);

export const Diamonds = ({ onNavigate }) => (
  <PageWrapper onNavigate={onNavigate} showFullFooter>
    <div className="page-hero" style={{ background: 'linear-gradient(135deg, #0d1a2d, #1a2d4d)' }}>
      <p className="section-label">Sector</p>
      <h1>Diamond Services</h1>
      <p>Ethical, transparent, and profitable diamond trade facilitation.</p>
    </div>
    <section className="section">
      <div className="container">
        <div className="grid-2" style={{ alignItems: 'start', gap: '3rem' }}>
          <div>
            <p className="section-label">Overview</p>
            <h2 className="section-title">Africa's Diamond Legacy</h2>
            <div className="gold-line" />
            <p style={{ color: 'var(--muted)', lineHeight: 1.9, marginBottom: '1.5rem' }}>Africa is home to some of the world's most significant diamond deposits. Varahi facilitates ethical sourcing, grading, and trade while ensuring full Kimberley Process compliance and maximum value for producers.</p>
            <ul className="service-list">
              {['Ethical diamond sourcing from certified mines', 'Kimberley Process certification compliance', 'Diamond grading, valuation, and certification', 'International market access and buyer network', 'Rough and polished diamond trade facilitation', 'Investment-grade diamond advisory', 'Artisanal miner support programs'].map(i => <li key={i}>{i}</li>)}
            </ul>
          </div>
          <div style={{ background: '#071a12', padding: '2.5rem' }}>
            <h3 style={{ color: '#c9a84c', fontFamily: "'Playfair Display', serif", marginBottom: '1.5rem' }}>Countries We Operate In</h3>
            {[['Botswana', "World's largest diamond producer by value"], ['Angola', 'Major rough diamond exporter'], ['Zimbabwe', 'Marange diamond fields operations'], ['Sierra Leone', 'Alluvial and kimberlite deposits'], ['DRC', 'Alluvial diamond production']].map(([c, d]) => (
              <div key={c} className="country-row">
                <div><h4>{c}</h4><p>{d}</p></div>
                <span className="country-badge">Active</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  </PageWrapper>
);

export const Renewables = ({ onNavigate }) => (
  <PageWrapper onNavigate={onNavigate} showFullFooter>
    <div className="page-hero" style={{ background: 'linear-gradient(135deg, #0d2d1a, #1a4d2d)' }}>
      <p className="section-label">Sector</p>
      <h1>Renewable Energy</h1>
      <p>Powering Africa's future with clean, sustainable energy solutions.</p>
    </div>
    <section className="section">
      <div className="container" style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <p className="section-label">The Opportunity</p>
        <h2 className="section-title" style={{ textAlign: 'center' }}>Africa's Green Energy Future</h2>
        <div className="gold-line center" />
        <p className="section-sub center">Africa receives more solar radiation than any other continent. With vast wind corridors and untapped hydro potential, the renewable energy opportunity is extraordinary.</p>
      </div>
      <div className="grid-3" style={{ maxWidth: '1100px', margin: '0 auto 4rem' }}>
        {[{ icon: '☀️', title: 'Solar Energy', top: '#f5c518', desc: 'Utility-scale solar PV and off-grid solutions. We handle site selection, permitting, financing, and EPC procurement for projects from 1MW to 500MW+.' }, { icon: '💨', title: 'Wind Energy', top: '#87ceeb', desc: 'Wind resource assessment, turbine procurement, and grid integration for Africa\'s high-potential wind corridors in East and Southern Africa.' }, { icon: '💧', title: 'Hydro Power', top: '#4682b4', desc: 'Small and large hydro project development, river assessment, environmental studies, and community benefit programs.' }].map(c => (
          <div key={c.title} className="card" style={{ borderTop: `4px solid ${c.top}` }}>
            <span className="card-icon">{c.icon}</span>
            <h3>{c.title}</h3>
            <p>{c.desc}</p>
          </div>
        ))}
      </div>
      <div style={{ background: '#071a12', padding: '4rem 3rem', textAlign: 'center' }}>
        <p className="section-label">Portfolio</p>
        <h2 className="section-title white" style={{ textAlign: 'center' }}>Our Energy Numbers</h2>
        <div className="gold-line center" />
        <div className="stats-row" style={{ marginTop: '2rem' }}>
          <div className="stat-box"><span className="stat-num">850MW</span><span className="stat-label">Facilitated</span></div>
          <div className="stat-box"><span className="stat-num">12</span><span className="stat-label">Countries</span></div>
          <div className="stat-box"><span className="stat-num">40+</span><span className="stat-label">Projects</span></div>
          <div className="stat-box"><span className="stat-num">2M+</span><span className="stat-label">Lives Impacted</span></div>
        </div>
      </div>
    </section>
  </PageWrapper>
);

export const EcoTourism = ({ onNavigate }) => (
  <PageWrapper onNavigate={onNavigate} showFullFooter>
    <div className="page-hero" style={{ background: 'linear-gradient(135deg, #1a2d0d, #2d4d1a)' }}>
      <p className="section-label">Sector</p>
      <h1>Eco Tourism</h1>
      <p>Where conservation meets commerce — building Africa's responsible tourism future.</p>
    </div>
    <section className="section">
      <div className="container">
        <div className="grid-2" style={{ alignItems: 'start', gap: '3rem' }}>
          <div>
            <p className="section-label">Overview</p>
            <h2 className="section-title">Conservation-Led Growth</h2>
            <div className="gold-line" />
            <p style={{ color: 'var(--muted)', lineHeight: 1.9, marginBottom: '1.5rem' }}>Africa's wildlife and wilderness are among humanity's greatest treasures. Varahi develops eco-tourism ventures that generate economic value for local communities while funding conservation efforts.</p>
            <ul className="service-list">
              {['Safari lodge concept development and investment structuring', 'Conservation area planning and wildlife management', 'Community-based tourism design and revenue sharing', 'International tour operator partnerships', 'Brand development and marketing strategy', 'Sustainable certification and ESG compliance'].map(i => <li key={i}>{i}</li>)}
            </ul>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {[{ icon: '🐘', title: 'Wildlife Destinations', desc: "Kenya's Maasai Mara, Tanzania's Serengeti, Botswana's Okavango Delta — we've developed ventures in Africa's premier wildlife zones." }, { icon: '🏔️', title: 'Adventure Tourism', desc: 'Mountain trekking, gorilla tracking, and adventure tourism infrastructure development across East Africa.' }, { icon: '🤝', title: 'Community Tourism', desc: 'Empowering indigenous communities to become stakeholders and beneficiaries of tourism development.' }].map(c => (
              <div key={c.title} className="card card-gold-top">
                <span className="card-icon">{c.icon}</span>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  </PageWrapper>
);
